import '.t{height:100%;}.ssr-plugin'
import '.f{width:100%;}.ssr-plugin'
import '.a{display:flex;}.ssr-plugin'
import '.d{align-items:center;}.ssr-plugin'
import '.am{overflow:hidden;}.ssr-plugin'
import '.iq{text-overflow:ellipsis;}.ssr-plugin'
import '.gd{white-space:nowrap;}.ssr-plugin'
import { styled } from 'inlines'
import { memo, useState, useCallback } from 'react'
import { colors } from '../../tokens/colors.js'
import { BasedNode, UiSchemaType } from '../../types/schema.js'
import { CheckboxInput } from '../CheckboxInput/index.js'
import { SchemaComponentMapping } from '../SchemaComponent/index.js'
import { RowAction, BulkAction } from './index.js'
import { ContextMenu } from '../ContextMenu/index.js'

export type TableRowProps = {
  index?: number
  rowRef?: React.MutableRefObject<any>
  rowData: BasedNode
  allRowsData?: BasedNode[]
  columns: UiSchemaType[]
  widths: number[]
  rowHeight?: 'regular' | 'large'
  selected?: boolean
  disabled?: boolean
  rowActions?: Record<string, RowAction | BulkAction>
  isHoverDisabled?: boolean
  selectableText?: boolean
  rowRole?: HTMLDivElement['role']
  cellRole?: HTMLDivElement['role']
  variant?: 'header' | 'row'
  checkboxVisibility?: 'aways' | 'hover' | 'never'
  actionsVisibility?: 'aways' | 'hover' | 'never'
  isHovered?: boolean
  onHover?: (index: number) => void
  onRowClick?: (values: BasedNode, value: any) => void
  onRowSelect?: (index: number, values: BasedNode) => void
}

export const ROW_HEIGHT: Record<TableRowProps['rowHeight'], number> = {
  regular: 44,
  large: 60,
}

export type TableCellProps = {
  children?: React.ReactNode
  role?: string
  cursor?: 'default' | 'pointer'
  selectableText?: boolean
  width?: number | string
  align?: 'left' | 'center' | 'right'
  onClick?: (value: any) => void
  noPadding?: boolean
}

export const TableCell: React.FC<TableCellProps> = memo(
  ({
    children,
    role,
    cursor,
    selectableText,
    width = '100%',
    align,
    noPadding = false,
    onClick,
  }) => {
    return (
      <styled.div className="t f a d am iq gd"
        role={role}
        onClick={onClick}
        style={{
          flex: `0 0 ${typeof width === 'string' ? width : `${width}px`}`,
          userSelect: selectableText ? 'text' : 'none',
          cursor,
          justifyContent:
            align === 'right'
              ? 'flex-end'
              : align === 'center'
                ? 'center'
                : 'flex-start',
          padding: noPadding ? '0' : '0 10px',
        }}
      >
        {children}
      </styled.div>
    )
  },
)

export const TableRow: React.FC<TableRowProps> = memo(
  ({
    index,
    rowRef,
    rowData,
    allRowsData,
    columns,
    widths,
    rowActions,
    rowHeight = 'regular',
    selected = false,
    disabled = false,
    isHoverDisabled = false,
    selectableText = false,
    rowRole = 'row',
    cellRole = 'cell',
    variant = 'row',
    checkboxVisibility = 'hover',
    actionsVisibility = 'hover',
    isHovered = false,
    onHover,
    onRowClick,
    onRowSelect,
  }) => {
    const handleClick = (value: any) => onRowClick?.(rowData, value)

    const [isMouseInside, setIsMouseInside] = useState(false)
    const hover = isMouseInside || isHovered

    const isCheckboxNeverVisible = checkboxVisibility === 'never'
    const isCheckboxVisibleOnHover = checkboxVisibility === 'hover'
    const isCheckboxAlwaysVisible = checkboxVisibility === 'aways'

    const isActionsNeverVisible = actionsVisibility === 'never'
    const isActionsVisibleOnHover = actionsVisibility === 'hover'
    const isActionsAlwaysVisible = actionsVisibility === 'aways'

    const isHeader = variant === 'header'
    const hasActions = Object.keys(rowActions || {}).length > 0

    const onMouseEnter = useCallback(() => {
      setIsMouseInside(true)
      onHover?.(index)
    }, [index, onHover])

    const onMouseLeave = useCallback(() => {
      setIsMouseInside(false)
      onHover?.(undefined)
    }, [onHover])

    const background =
      !isHeader && !isHoverDisabled
        ? hover && selected
          ? colors.neutral20Adjusted
          : (!hover && selected) || (hover && !selected)
            ? colors.neutral10Adjusted
            : 'transparent'
        : undefined

    return (
      <styled.div className="a d f"
        role={rowRole}
        ref={rowRef}
        style={{
          background,
          opacity: disabled ? 0.2 : 1,
          height: ROW_HEIGHT[rowHeight],}}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        {/* checkbox */}
        {!isCheckboxNeverVisible && (
          <TableCell key={'checkbox-' + index} width={44} align="center">
            {(isCheckboxVisibleOnHover && hover) ||
            isCheckboxAlwaysVisible ||
            selected ? (
              <CheckboxInput
                size="small"
                value={selected}
                disabled={disabled}
                onChange={() => {
                  onRowSelect?.(index, rowData)
                }}
              />
            ) : undefined}
          </TableCell>
        )}

        {/* columns */}
        {columns.map((col, colIndex) => {
          return (
            <TableCell
              key={'cell-' + col.key + '-' + colIndex}
              role={cellRole}
              onClick={() => !disabled && handleClick(rowData[col.key])}
              cursor={
                disabled ||
                String(rowData[col.key])?.toLowerCase() === 'id' ||
                !onRowClick
                  ? 'default'
                  : 'pointer'
              }
              selectableText={selectableText}
              width={widths[colIndex]}
              align={col?.prop?.align}
              noPadding={col?.prop?.render ? true : false}
            >
              <SchemaComponentMapping
                schemaProp={col?.prop}
                colKey={col.key}
                cellData={rowData[col.key]}
                rowData={rowData}
                allRowsData={allRowsData}
                maxWidth={widths[colIndex] || 250}
              />
            </TableCell>
          )
        })}

        {/* row actions */}
        {hasActions && (
          <TableCell key={'actions-' + index} width={44} align="center">
            {!isActionsNeverVisible &&
              ((isActionsVisibleOnHover && hover) ||
                isActionsAlwaysVisible) && (
                <ContextMenu
                  size="small"
                  position="left"
                  items={rowActions}
                  value={{ index, values: rowData }}
                />
              )}
          </TableCell>
        )}
      </styled.div>
    )
  },
)
